import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, defaultIfEmpty, from, map, of, switchMap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { AUTH_TOKEN_LOCAL_STORAGE_KEY } from '../config/auth-config';
import { AuthSystem } from './auth-system';

/**
 * Login por email e password contra o backend (POST users/login).
 *
 * O backend devolve um token de sessao (JWT, 12 h) que fica no localStorage e
 * vai no cabecalho Authorization de cada pedido (ver AuthInterceptor). Expirado
 * o token, a proxima chamada recebe 401 e a app volta ao login.
 */
export class AuthWithPassword extends AuthSystem {
  router: Router = inject(Router);

  constructor() {
    super();
  }

  /** Entra com email e password; true se o backend aceitou. */
  login(email: string, password: string): Observable<boolean> {
    return this.api.post<any>('users/login', { email, password }, { noShowError: true, noLogout: true }).pipe(
      defaultIfEmpty(null),
      catchError(() => of(null)),
      switchMap((res: any) => {
        if (!res?.token) return of(false);
        this.storeToken(res.token);
        return this.signIn();
      })
    );
  }

  /** Retoma a sessao guardada, se o token ainda for valido. */
  override signIn(): Observable<boolean> {
    const token = this.storedToken();
    if (!token || !this.isTokenValid(token)) {
      this.clearToken();
      return of(false);
    }

    this.userAuthToken$.next(token);
    return from(this.retrieveUserFromLogin2()).pipe(
      map((user) => {
        if (!user) return false;
        this.setAuthType(user.type);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  override signOut(): void {
    this.clearToken();
    this.removeAuthType();
    this.user$.next(null);
    this.userService.user$.next(null);
    setTimeout(() => this.router.navigate(['/']));
  }

  override isAuthTokenValid(): boolean {
    return this.isTokenValid(this.userAuthToken$.getValue());
  }

  override idToken(): Observable<string | null> {
    const token = this.storedToken();
    return of(token && this.isTokenValid(token) ? token : null);
  }

  private isTokenValid(token: string | null): boolean {
    if (!token) return false;
    try {
      const decoded: any = jwtDecode(token);
      return !!decoded?.exp && decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  private storedToken(): string | null {
    try {
      return localStorage.getItem(AUTH_TOKEN_LOCAL_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeToken(token: string): void {
    try {
      localStorage.setItem(AUTH_TOKEN_LOCAL_STORAGE_KEY, token);
    } catch { }
    this.userAuthToken$.next(token);
  }

  private clearToken(): void {
    try {
      localStorage.removeItem(AUTH_TOKEN_LOCAL_STORAGE_KEY);
    } catch { }
    this.userAuthToken$.next('');
  }
}
